import React from 'react';
import { withFormik } from 'formik';
import Papa from 'papaparse';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import UploadHostedCSVForm from './UploadHostedCSVForm';
import { HOSTED_TABLE_TABS } from './HostedLookupTablesList';

const UploadHostedCSV = ({
  cancelForm,
  setHostedTablsData,
  maxColumnsCount,
  maxRowsCount,
  showPreview,
  activeTab,
  isEdit,
  tableInfo
}) => {
  const tableName = useSelector((state) => state.hostedTables.tableName);
  const uploadData = useSelector((state) => state.hostedTables.uploadData);
  const containsHeader = useSelector((state) => state.hostedTables.containsHeader);
  const numberOfColumn = useSelector((state) => state.hostedTables.numberOfColumn);
  const delimiter = useSelector((state) => state.hostedTables.delimiter);
  const selectedTableData = useSelector((state) => state.hostedTables.selectedTableData);
  const file = useSelector((state) => state.hostedTables.file);

  const kFormatter = (num) => {
    return Math.abs(num) > 999
      ? Math.sign(num) * (Math.abs(num) / 1000).toFixed(1) + 'K'
      : Math.sign(num) * Math.abs(num);
  };

  const readCSV = (file, delimiter) => {
    return new Promise((resolve) => {
      Papa.parse(file, {
        header: false,
        dynamicTyping: true,
        delimiter,
        complete: function (results) {
          resolve(results);
        }
      });
    });
  };

  const validate = async (values) => {
    const errors = {};
    const basicStringRegEx = activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES ?  /^[a-zA-Z0-9_ ]*$/g : /^[a-zA-Z0-9_]*$/g;
    const file = values.file;

    if (values.containsHeader) {
      maxRowsCount = maxRowsCount + 1;
    }

    if (values.tableName.trim() === '') {
      errors['tableName'] = 'Table name is Required';
    } else if (!basicStringRegEx.test(values.tableName)) {
      errors['tableName'] = 'Table name may only contain alphanumeric characters or underscore';
    }

    if (!values.uploadData) {
      if (
        isNaN(values.numberOfColumn) ||
        (typeof values.numberOfColumn === 'string' && values.numberOfColumn.trim() === '')
      ) {
        errors['numberOfColumn'] = 'Number of Columns must be number';
      } else if (parseInt(values.numberOfColumn) > maxColumnsCount) {
        errors['numberOfColumn'] = 'Number of Columns must not be greater than ' + maxColumnsCount;
      } else if (parseInt(values.numberOfColumn) < 1) {
        errors['numberOfColumn'] = 'Number of Columns must not be less than 1';
      }
    }

    if (values.uploadData) {
      if (!file) {
        errors['file'] = 'CSV file is Required';
      } else if (file && file.type.toLowerCase() !== 'text/csv') {
        errors['file'] = 'Only CSV file is allowed';
      } else {
        try {
          const parsed = await readCSV(file, values.delimiter);
          const csvData = parsed.data;
          const emptyValues = _.filter(csvData[0], val => _.isNil(val) || val.trim?.() === '');

          const maxColCount = (activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES && isEdit)
            ? maxColumnsCount + 1
            : maxColumnsCount;

          if (parsed.errors.length > 0 && csvData.length && csvData[0].length !== 1) {
            errors['file'] = 'CSV must contain valid data';
          } else if (csvData.length > maxRowsCount) {
            errors['file'] = 'CSV should not contain records more than ' + kFormatter(maxRowsCount);
          } else if (csvData[0].length > maxColCount) {
            errors['file'] = 'CSV should not contain columns more than ' + maxColumnsCount;
          } else if (emptyValues.length > 0) {
            errors['file'] = 'CSV must contain valid data. Column name can not be empty';
          }
          else if (
            activeTab === HOSTED_TABLE_TABS.CONVERSATIONAL_TABLES &&
            isEdit &&
            selectedTableData?.columnDescriptions
          ) {
            const selectedTableCols = selectedTableData.columnDescriptions.map((dt) => dt.columnName);
            const csvDtFields = csvData[0];
            // .trim().replace(/\s+/g, "_").toLowerCase()
            const lowerCsvCols = csvDtFields.map(col => col.trim().replace(/\s+/g, "_").toLowerCase());
            const missing = selectedTableCols.filter(
              col => col.toLowerCase() !== 'content_id' && !lowerCsvCols.includes(col.toLowerCase())
            );
            const expected = selectedTableCols.filter(col => col.toLowerCase() !== 'content_id');
            const extra = lowerCsvCols.filter(
              col => !expected.map(c => c.toLowerCase()).includes(col)
            );

            if (missing.length > 0 || extra.length > 0) {
              errors['file'] = {
                message: 'CSV must contain valid data. CSV columns do not match expected table columns.',
                csvColumns: csvDtFields,
                missing,
                extra,
                expected,
              };

              if (extra.length > 0 && missing.length === 0) {
                errors['file'].message = 'CSV contains more columns than expected.';
              } else if (missing.length > 0 && extra.length === 0) {
                errors['file'].message = 'CSV is missing expected columns.';
              } else {
                errors['file'].message = 'CSV columns mismatch: missing or extra columns detected.';
              }
            }
          }
        } catch (err) {
          errors['file'] = 'Error reading CSV file';
        }
      }
    }

    if (values.containsHeader) {
      maxRowsCount = maxRowsCount - 1;
    }

    return errors;
  };

  const initialValues = {
    tableName,
    file,
    containsHeader,
    numberOfColumn,
    uploadData,
    delimiter
  };

  const FormikForm = withFormik({
    mapPropsToValues: () => initialValues,
    handleSubmit: (rawValues) => {
      setHostedTablsData({ ...rawValues, step: 2, isEdit });
    },
    validate,
    initialValues,
    enableReinitialize: true
  })(UploadHostedCSVForm);

  return (
    <FormikForm
      cancelForm={cancelForm}
      showPreview={showPreview}
      activeTab={activeTab}
      isEdit={isEdit}
      selectedTableData={selectedTableData}
      kFormatter={kFormatter}
      maxRowsCount={maxRowsCount}
      maxColumnsCount={maxColumnsCount}
    />
  );
};

export default UploadHostedCSV;
